import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Text,
  Button,
  Flex,
  Spinner,
  Badge,
  HStack,
  VStack,
  Input,
  Select,
  Heading,
  IconButton,
  Image,
  Wrap,
  WrapItem,
  useDisclosure,
  useToast,
  Divider,
} from "@chakra-ui/react";
import { Plus, Trash2, Pencil } from "lucide-react";
import {
  useGetProductsQuery,
  useCreateProductMutation,
  useDeleteProductMutation,
  useUpdateProductMutation,
} from "../../productApi/productAPI";
import { Product } from "../../types/product";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../configs/firebase";
import { useAuth } from "../../auth/AuthContext";
import { useCustomTheme } from "../../contexts/CustomThemeContext";
import AppPagination from "../Pagination/AppPagination";
import { AdminDataTable } from "../DataTable/AdminDataTable";
import { ProductFormModal } from "./ProductFormModal";
import GlobalSpinner from "../CustomReusables/GlobalSpinner";

const cleanAccessValue = (value: unknown) => String(value || "").trim();

const normalizeAccessRole = (value: unknown) =>
  cleanAccessValue(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

const getInlineAccessRole = (user: any) =>
  user?.role ||
  user?.userRole ||
  user?.accountRole ||
  user?.adminRole ||
  user?.claims?.role ||
  user?.customClaims?.role ||
  "";

const isSuperAdminRole = (value: unknown) =>
  normalizeAccessRole(value) === "superadmin";

const getProductOwnerId = (product?: Product | null) =>
  cleanAccessValue(
    (product as any)?.sellerUserId ||
      (product as any)?.ownerId ||
      (product as any)?.ownerUserId ||
      (product as any)?.userId,
  );

const ProductAdminPage: React.FC = () => {
  const toast = useToast();
  const { user } = useAuth();

  const currentUserUid = cleanAccessValue(user?.uid);
  const [resolvedAccessRole, setResolvedAccessRole] = useState(() =>
    getInlineAccessRole(user),
  );
  const [accessLoading, setAccessLoading] = useState(true);
  const isSuperAdmin = isSuperAdminRole(resolvedAccessRole);

  const { data: products = [], isLoading, refetch } = useGetProductsQuery();
  const [createProduct] = useCreateProductMutation();
  const [updateProduct] = useUpdateProductMutation();
  const [deleteProduct] = useDeleteProductMutation();
  const [deletingProductId, setDeletingProductId] = useState<string | null>(
    null,
  );

  const [search, setSearch] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");

  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  const createModal = useDisclosure();
  const editModal = useDisclosure();

  const { theme } = useCustomTheme();
  const isDark = theme.name === "dark";

  const brand = theme.brand || {
    electricBlue: "#27b7ff",
    indigo: "#6f7cff",
    violet: "#b05cff",
  };

  const gradients = theme.gradients || {
    pageBg: theme.background,
    shellBg: theme.surface,
    primaryButton: `linear-gradient(135deg, ${brand.electricBlue} 0%, #49d1ff 100%)`,
    primaryButtonHover: `linear-gradient(135deg, #1eaef3 0%, #2cc2ff 100%)`,
  };

  const pageBg = gradients.pageBg || theme.background;
  const shellBg = gradients.shellBg || theme.surface;
  const cardBg = theme.surface;
  const softBg = theme.surfaceAlt;
  const textColor = theme.text;
  const mutedText = theme.textMuted;
  const subtleText = theme.textSubtle;
  const panelShadow = theme.shadowMd;
  const softShadow = theme.shadowSm;
  const cardsBorderRadii = "8px";

  const baseControlStyle = {
    border: `1px solid ${theme.border}`,
    borderRadius: { cardsBorderRadii },
    boxShadow: softShadow,
    bg: isDark ? "rgba(255,255,255,0.035)" : "rgba(255,255,255,0.92)",
    color: textColor,
    _hover: {
      borderColor: brand.electricBlue,
      bg: isDark ? "rgba(255,255,255,0.06)" : "rgba(255,255,255,1)",
    },
    _focus: {
      borderColor: brand.electricBlue,
      boxShadow: `0 0 0 1px ${brand.electricBlue}`,
    },
  };

  const primaryButtonStyles = {
    color: theme.textOnPrimary,
    bgImage: theme.gradients?.primaryButton || theme.primary,
    boxShadow: theme.shadowSm,
    _hover: {
      bgImage: theme.gradients?.primaryButtonHover || theme.primaryHover,
      transform: "translateY(-1px)",
      boxShadow: theme.shadowMd,
    },
    _active: { transform: "translateY(0)" },
    borderRadius: { cardsBorderRadii },
    transition: "all 0.2s ease",
  };

  const secondaryButtonStyles = {
    color: textColor,
    bg: isDark ? "rgba(255,255,255,0.04)" : "white",
    border: `1px solid ${theme.border}`,
    _hover: {
      bg: isDark ? "rgba(255,255,255,0.07)" : "rgba(42,44,59,0.03)",
    },
    borderRadius: { cardsBorderRadii },
    transition: "all 0.2s ease",
  };

  const dangerButtonStyles = {
    color: "white",
    bg: theme.danger,
    _hover: {
      filter: "brightness(0.95)",
      transform: "translateY(-1px)",
      boxShadow: theme.shadowSm,
    },
    _active: { transform: "translateY(0)" },
    borderRadius: { cardsBorderRadii },
    transition: "all 0.2s ease",
  };

  useEffect(() => {
    let cancelled = false;

    const loadAccess = async () => {
      if (!user?.uid) {
        if (!cancelled) {
          setResolvedAccessRole("");
          setAccessLoading(false);
        }
        return;
      }

      setAccessLoading(true);

      try {
        const snapshot = await getDoc(doc(db, "users", user.uid));
        const profile = snapshot.exists() ? snapshot.data() : {};
        const role =
          profile?.role || profile?.userRole || getInlineAccessRole(user);

        if (!cancelled) setResolvedAccessRole(String(role || ""));
      } catch (accessError) {
        console.error("Failed to resolve Product Admin access:", accessError);

        if (!cancelled) {
          setResolvedAccessRole(String(getInlineAccessRole(user) || ""));
        }
      } finally {
        if (!cancelled) setAccessLoading(false);
      }
    };

    void loadAccess();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const scopedProducts = useMemo(() => {
    const allProducts = Array.isArray(products) ? (products as Product[]) : [];

    if (isSuperAdmin) return allProducts;
    if (!currentUserUid) return [];

    return allProducts.filter(
      (product) => getProductOwnerId(product) === currentUserUid,
    );
  }, [currentUserUid, isSuperAdmin, products]);

  const canManageProduct = (product?: Product | null) =>
    Boolean(product) &&
    (isSuperAdmin || getProductOwnerId(product) === currentUserUid);

  const showAccessDenied = () => {
    toast({
      title: "Access denied",
      description: "You can only manage products linked to your account.",
      status: "error",
      duration: 3500,
      isClosable: true,
    });
  };

  const categoryOptions = useMemo(() => {
    return Array.from(
      new Set(
        scopedProducts.map((p) => (p.category || "").trim()).filter(Boolean),
      ),
    ).sort((a, b) => a.localeCompare(b));
  }, [scopedProducts]);

  const filteredProducts = useMemo(() => {
    let data = [...scopedProducts];
    const q = search.trim().toLowerCase();

    if (q) {
      data = data.filter((p: any) => {
        const title = (p.title || "").toLowerCase();
        const category = (p.category || "").toLowerCase();
        const desc = (p.description || "").toLowerCase();
        const id = (p._id || "").toLowerCase();
        const tags = Array.isArray(p.tags)
          ? p.tags.join(" ").toLowerCase()
          : "";
        const variants = Array.isArray(p.variantAttributes)
          ? p.variantAttributes
              .map(
                (attr: any) =>
                  `${attr?.name || ""} ${(attr?.options || [])
                    .map((opt: any) => opt?.label || opt?.value || "")
                    .join(" ")}`,
              )
              .join(" ")
              .toLowerCase()
          : "";

        return (
          title.includes(q) ||
          category.includes(q) ||
          desc.includes(q) ||
          id.includes(q) ||
          tags.includes(q) ||
          variants.includes(q)
        );
      });
    }

    if (categoryFilter !== "all") {
      data = data.filter((p) => (p.category || "") === categoryFilter);
    }

    data.sort((a: any, b: any) => {
      if (sortBy === "title-asc")
        return (a.title || "").localeCompare(b.title || "");
      if (sortBy === "title-desc")
        return (b.title || "").localeCompare(a.title || "");
      if (sortBy === "price-high")
        return Number(b.price || 0) - Number(a.price || 0);
      if (sortBy === "price-low")
        return Number(a.price || 0) - Number(b.price || 0);

      const aDate = a.createdAt ? new Date(a.createdAt).getTime() : 0;
      const bDate = b.createdAt ? new Date(b.createdAt).getTime() : 0;
      if (sortBy === "oldest") return aDate - bDate;
      return bDate - aDate;
    });

    return data;
  }, [scopedProducts, search, categoryFilter, sortBy]);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const totalItems = filteredProducts.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));

  const paginatedItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredProducts.slice(startIndex, endIndex);
  }, [filteredProducts, currentPage, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const handleDelete = async (id?: string) => {
    if (!id) return;

    const product = (products as Product[]).find(
      (item) => cleanAccessValue(item._id) === cleanAccessValue(id),
    );

    if (!canManageProduct(product)) {
      showAccessDenied();
      return;
    }

    setDeletingProductId(id);

    try {
      await deleteProduct(id).unwrap();
      toast({
        title: "Product deleted",
        status: "success",
        duration: 2000,
        isClosable: true,
      });
      await refetch();
    } catch (err) {
      console.error(err);
      toast({
        title: "Failed to delete product",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeletingProductId(null);
    }
  };

  const handleCreate = async (payload: Partial<Product>) => {
    if (!currentUserUid) {
      throw new Error("A signed-in account is required to create a product.");
    }

    await createProduct({
      ...payload,
      userId: currentUserUid,
    } as Product).unwrap();

    await refetch();
  };

  const handleUpdate = async (payload: Partial<Product>) => {
    if (!editingProduct?._id) throw new Error("Missing product id");

    if (!canManageProduct(editingProduct)) {
      showAccessDenied();
      throw new Error("You cannot update another account's product.");
    }

    const existingOwnerId =
      cleanAccessValue((editingProduct as any)?.userId) ||
      getProductOwnerId(editingProduct) ||
      currentUserUid;

    await updateProduct({
      id: editingProduct._id,
      updatedProduct: {
        ...payload,
        // Never transfer ownership as a side effect of opening the edit modal.
        userId: existingOwnerId,
      },
    }).unwrap();

    await refetch();
  };

  const openEdit = (product: Product) => {
    if (!canManageProduct(product)) {
      showAccessDenied();
      return;
    }

    setEditingProduct(product);
    editModal.onOpen();
  };

  const getProductImages = (p: Product): string[] => {
    const many = Array.isArray((p as any).images)
      ? ((p as any).images as string[])
      : [];
    const single = p.imageUrl ? [p.imageUrl] : [];
    return Array.from(new Set([...many, ...single].filter(Boolean)));
  };

  const getVariantSummary = (p: any) => {
    const attrs = Array.isArray(p?.variantAttributes)
      ? p.variantAttributes
      : [];
    const totalOptions = attrs.reduce(
      (sum: number, attr: any) =>
        sum + (Array.isArray(attr?.options) ? attr.options.length : 0),
      0,
    );

    return {
      attributeCount: attrs.length,
      totalOptions,
      attributes: attrs,
    };
  };

  return (
    <Box h="100%" minH="100%" w="100%" bg={pageBg}>
      <VStack h="100%" minH="100%" w="100%" spacing={0} align="stretch">
        <Box
          flex="1"
          minH={0}
          w="100%"
          borderRadius={{ base: cardsBorderRadii, md: cardsBorderRadii }}
          bg={shellBg}
          boxShadow={panelShadow}
          p={{ base: 2, md: 3 }}
          backdropFilter={isDark ? "blur(10px)" : undefined}
        >
          <Flex
            justify="space-between"
            align={{ base: "flex-start", md: "center" }}
            direction={{ base: "column", md: "row" }}
            gap={3}
            mb={3}
          >
            <Box>
              <Heading size="lg" color={textColor}>
                Product Admin
              </Heading>
              <Text color={mutedText} fontWeight="500" mt={1}>
                Manage products with images, optional colors/sizes, search,
                filters and pagination.
              </Text>
            </Box>

            <HStack spacing={2} flexWrap="wrap">
              <HStack
                px={3}
                py={2}
                borderRadius={cardsBorderRadii}
                border={`1px solid ${theme.border}`}
                bg={isDark ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.8)"}
                boxShadow={softShadow}
                spacing={2}
              >
                <Box
                  w="8px"
                  h="8px"
                  borderRadius={cardsBorderRadii}
                  bg={brand.electricBlue}
                />
                <Text color={textColor} fontWeight="700" fontSize="sm">
                  {scopedProducts.length} products
                </Text>
              </HStack>

              <Button
                leftIcon={<Plus size={16} />}
                onClick={createModal.onOpen}
                size="sm"
                {...primaryButtonStyles}
              >
                Add Product
              </Button>
            </HStack>
          </Flex>

          {isLoading || accessLoading ? (
            <Flex
              minH="240px"
              justify="center"
              align="center"
              direction="column"
              gap={3}
            >
              <GlobalSpinner />
              <Text color={mutedText} fontWeight="500">
                Loading products...
              </Text>
            </Flex>
          ) : (
            <>
              <Box
                borderRadius={cardsBorderRadii}
                bg={cardBg}
                boxShadow={softShadow}
                border={`1px solid ${theme.border}`}
                p={{ base: 3, md: 4 }}
                mb={3}
                position="relative"
                overflow="hidden"
                _before={{
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  height: "3px",
                  bg: gradients.primaryButton || brand.electricBlue,
                }}
              >
                <Flex
                  gap={3}
                  wrap="wrap"
                  align="center"
                  direction={{ base: "column", md: "row" }}
                >
                  <Input
                    placeholder="Search title, category, description, ID or variant option..."
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setCurrentPage(1);
                    }}
                    maxW={{ base: "100%", md: "360px" }}
                    {...baseControlStyle}
                  />

                  <Select
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setCurrentPage(1);
                    }}
                    maxW={{ base: "100%", md: "200px" }}
                    {...baseControlStyle}
                    sx={{
                      option: {
                        background: isDark ? "#1f2430" : "#fff",
                        color: isDark ? "#f7fafc" : "#1a202c",
                      },
                    }}
                  >
                    <option value="all">All categories</option>
                    {categoryOptions.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </Select>

                  <Select
                    value={sortBy}
                    onChange={(e) => {
                      setSortBy(e.target.value);
                      setCurrentPage(1);
                    }}
                    maxW={{ base: "100%", md: "190px" }}
                    {...baseControlStyle}
                    sx={{
                      option: {
                        background: isDark ? "#1f2430" : "#fff",
                        color: isDark ? "#f7fafc" : "#1a202c",
                      },
                    }}
                  >
                    <option value="newest">Sort: Newest</option>
                    <option value="oldest">Sort: Oldest</option>
                    <option value="title-asc">Title: A → Z</option>
                    <option value="title-desc">Title: Z → A</option>
                    <option value="price-high">Price: High → Low</option>
                    <option value="price-low">Price: Low → High</option>
                  </Select>

                  <HStack
                    ml={{ md: "auto" }}
                    spacing={2}
                    px={3}
                    py={2}
                    borderRadius={cardsBorderRadii}
                    border={`1px solid ${theme.border}`}
                    bg={isDark ? "rgba(255,255,255,0.02)" : softBg}
                    boxShadow={softShadow}
                    w={{ base: "100%", md: "auto" }}
                  >
                    <Box
                      w="7px"
                      h="7px"
                      borderRadius={cardsBorderRadii}
                      bg={brand.indigo}
                    />
                    <Text color={mutedText} fontSize="sm" fontWeight="600">
                      Filtered:{" "}
                      <Text as="span" color={textColor}>
                        {filteredProducts.length}
                      </Text>{" "}
                      / {products.length}
                    </Text>
                  </HStack>
                </Flex>
              </Box>

              <Box display={{ base: "none", md: "block" }} mb={3}>
                <AdminDataTable<Product>
                  rows={paginatedItems}
                  rowKey={(row) => row._id || row.title}
                  minW="940px"
                  emptyText="No products found"
                  columns={[
                    {
                      key: "product",
                      header: "Product",
                      width: "260px",
                      render: (p: any) => {
                        const imgs = getProductImages(p);
                        return (
                          <HStack spacing={3} align="center">
                            <Image
                              src={
                                imgs[0] ||
                                "https://picsum.photos/seed/product-admin/100/100"
                              }
                              alt={p.title}
                              boxSize="44px"
                              objectFit="cover"
                              borderRadius={cardsBorderRadii}
                              border={`1px solid ${theme.border}`}
                              flexShrink={0}
                            />
                            <Box minW={0}>
                              <Text
                                color={textColor}
                                fontWeight="700"
                                noOfLines={1}
                              >
                                {p.title}
                              </Text>
                              <Text
                                color={subtleText}
                                fontSize="xs"
                                mt={0.5}
                                noOfLines={1}
                              >
                                ID: {p._id}
                              </Text>
                            </Box>
                          </HStack>
                        );
                      },
                    },
                    {
                      key: "category",
                      header: "Category",
                      width: "140px",
                      render: (p: any) => (
                        <Badge
                          px={2.5}
                          py={1}
                          borderRadius={cardsBorderRadii}
                          bg={
                            isDark
                              ? "rgba(111,124,255,0.16)"
                              : "rgba(111,124,255,0.10)"
                          }
                          color={isDark ? "purple.200" : "purple.700"}
                          textTransform="capitalize"
                        >
                          {p.category || "uncategorized"}
                        </Badge>
                      ),
                    },
                    {
                      key: "price",
                      header: "Price",
                      width: "120px",
                      render: (p: any) => (
                        <Text color={textColor} fontWeight="700">
                          MWK {Number(p.price || 0).toLocaleString()}
                        </Text>
                      ),
                    },
                    {
                      key: "variants",
                      header: "Options",
                      width: "230px",
                      render: (p: any) => {
                        const summary = getVariantSummary(p);

                        if (summary.attributeCount === 0) {
                          return (
                            <Text color={subtleText} fontSize="sm">
                              No options
                            </Text>
                          );
                        }

                        return (
                          <VStack align="start" spacing={1}>
                            <HStack spacing={2} flexWrap="wrap">
                              <Badge colorScheme="green">
                                {summary.attributeCount} type
                                {summary.attributeCount === 1 ? "" : "s"}
                              </Badge>
                              <Badge colorScheme="blue">
                                {summary.totalOptions} option
                                {summary.totalOptions === 1 ? "" : "s"}
                              </Badge>
                            </HStack>
                            <Wrap spacing={1}>
                              {summary.attributes
                                .slice(0, 3)
                                .map((attr: any) => (
                                  <WrapItem key={attr.name}>
                                    <Badge
                                      bg={theme.surfaceAlt}
                                      color={theme.text}
                                      border={`1px solid ${theme.border}`}
                                    >
                                      {attr.name}
                                    </Badge>
                                  </WrapItem>
                                ))}
                            </Wrap>
                          </VStack>
                        );
                      },
                    },
                    {
                      key: "images",
                      header: "Images",
                      width: "120px",
                      render: (p: any) => {
                        const imgs = getProductImages(p);
                        return imgs.length ? (
                          <HStack spacing={2}>
                            {imgs.slice(0, 2).map((src, i) => (
                              <Image
                                key={`${src}-${i}`}
                                src={src}
                                alt={`${p.title}-${i}`}
                                boxSize="34px"
                                objectFit="cover"
                                borderRadius={cardsBorderRadii}
                                border={`1px solid ${theme.border}`}
                              />
                            ))}
                            {imgs.length > 2 && (
                              <Badge colorScheme="blue">
                                +{imgs.length - 2}
                              </Badge>
                            )}
                          </HStack>
                        ) : (
                          <Text color={subtleText} fontSize="sm">
                            No images
                          </Text>
                        );
                      },
                    },
                    {
                      key: "actions",
                      header: "Actions",
                      width: "120px",
                      render: (p: any) => (
                        <HStack justify="flex-end" spacing={2}>
                          <IconButton
                            aria-label="Edit product"
                            icon={<Pencil size={16} />}
                            size="sm"
                            onClick={() => openEdit(p)}
                            {...secondaryButtonStyles}
                          />
                          <IconButton
                            aria-label="Delete product"
                            icon={<Trash2 size={16} />}
                            size="sm"
                            onClick={() => handleDelete(p._id)}
                            isLoading={
                              Boolean(p._id) && deletingProductId === p._id
                            }
                            {...dangerButtonStyles}
                          />
                        </HStack>
                      ),
                    },
                  ]}
                />
              </Box>

              <VStack
                spacing={4}
                align="stretch"
                display={{ base: "flex", md: "none" }}
                mb={3}
              >
                {paginatedItems.length === 0 ? (
                  <Box
                    borderRadius={cardsBorderRadii}
                    bg={cardBg}
                    boxShadow={softShadow}
                    border={`1px solid ${theme.border}`}
                    p={8}
                    textAlign="center"
                  >
                    <Text color={textColor} fontWeight="700">
                      No products found
                    </Text>
                    <Text color={mutedText} mt={1}>
                      Try changing your search or filter.
                    </Text>
                  </Box>
                ) : (
                  paginatedItems.map((product: any) => {
                    const imgs = getProductImages(product);
                    const summary = getVariantSummary(product);

                    return (
                      <Box
                        key={product._id}
                        borderRadius={cardsBorderRadii}
                        bg={cardBg}
                        boxShadow={softShadow}
                        border={`1px solid ${theme.border}`}
                        p={4}
                        position="relative"
                        overflow="hidden"
                        _before={{
                          content: '""',
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: "3px",
                          bg: gradients.primaryButton || brand.electricBlue,
                        }}
                      >
                        <HStack align="start" spacing={3}>
                          <Image
                            src={
                              imgs[0] ||
                              "https://picsum.photos/seed/product-mobile/100/100"
                            }
                            alt={product.title}
                            boxSize="64px"
                            objectFit="cover"
                            borderRadius={cardsBorderRadii}
                            border={`1px solid ${theme.border}`}
                            flexShrink={0}
                          />

                          <Box flex={1} minW={0}>
                            <Text
                              color={textColor}
                              fontWeight="700"
                              noOfLines={1}
                            >
                              {product.title}
                            </Text>

                            <HStack mt={2} spacing={2} flexWrap="wrap">
                              <Badge
                                px={2.5}
                                py={1}
                                borderRadius={cardsBorderRadii}
                                bg={
                                  isDark
                                    ? "rgba(111,124,255,0.16)"
                                    : "rgba(111,124,255,0.10)"
                                }
                                color={isDark ? "purple.200" : "purple.700"}
                              >
                                {product.category || "uncategorized"}
                              </Badge>

                              <Text
                                color={textColor}
                                fontWeight="700"
                                fontSize="sm"
                              >
                                MWK{" "}
                                {Number(product.price || 0).toLocaleString()}
                              </Text>
                            </HStack>
                          </Box>
                        </HStack>

                        <Text
                          color={mutedText}
                          fontSize="sm"
                          mt={3}
                          noOfLines={2}
                        >
                          {product.description || "—"}
                        </Text>

                        <HStack mt={3} spacing={2} flexWrap="wrap">
                          <Badge
                            colorScheme={
                              summary.attributeCount > 0 ? "green" : "gray"
                            }
                          >
                            {summary.attributeCount} option type
                            {summary.attributeCount === 1 ? "" : "s"}
                          </Badge>
                          <Badge
                            colorScheme={
                              summary.totalOptions > 0 ? "blue" : "gray"
                            }
                          >
                            {summary.totalOptions} option
                            {summary.totalOptions === 1 ? "" : "s"}
                          </Badge>
                          <Badge colorScheme="purple">
                            {imgs.length} image{imgs.length === 1 ? "" : "s"}
                          </Badge>
                        </HStack>

                        {summary.attributeCount > 0 && (
                          <Wrap mt={3} spacing={2}>
                            {summary.attributes.map((attr: any) => (
                              <WrapItem key={attr.name}>
                                <Badge
                                  px={2.5}
                                  py={1}
                                  borderRadius={cardsBorderRadii}
                                  bg={theme.surfaceAlt}
                                  color={theme.text}
                                  border={`1px solid ${theme.border}`}
                                >
                                  {attr.name}: {(attr.options || []).length}
                                </Badge>
                              </WrapItem>
                            ))}
                          </Wrap>
                        )}

                        {imgs.length > 1 && (
                          <Wrap mt={3} spacing={2}>
                            {imgs.slice(0, 4).map((src, i) => (
                              <WrapItem key={`${src}-${i}`}>
                                <Image
                                  src={src}
                                  alt={`${product.title}-${i}`}
                                  boxSize="42px"
                                  objectFit="cover"
                                  borderRadius={cardsBorderRadii}
                                  border={`1px solid ${theme.border}`}
                                />
                              </WrapItem>
                            ))}
                            {imgs.length > 4 && (
                              <WrapItem>
                                <Badge colorScheme="blue">
                                  +{imgs.length - 4}
                                </Badge>
                              </WrapItem>
                            )}
                          </Wrap>
                        )}

                        <Divider my={4} borderColor={theme.border} />

                        <HStack spacing={2}>
                          <Button
                            leftIcon={<Pencil size={14} />}
                            size="sm"
                            flex={1}
                            onClick={() => openEdit(product)}
                            {...secondaryButtonStyles}
                          >
                            Edit
                          </Button>
                          <Button
                            leftIcon={<Trash2 size={14} />}
                            size="sm"
                            flex={1}
                            onClick={() => handleDelete(product._id)}
                            isLoading={
                              Boolean(product._id) &&
                              deletingProductId === product._id
                            }
                            {...dangerButtonStyles}
                          >
                            Delete
                          </Button>
                        </HStack>
                      </Box>
                    );
                  })
                )}
              </VStack>

              <AppPagination
                totalItems={totalItems}
                currentPage={currentPage}
                pageSize={pageSize}
                onPageChange={setCurrentPage}
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
                pageSizeOptions={[5, 10, 20, 50]}
                showPageSizeSelector
                showSummary
              />
            </>
          )}
        </Box>
      </VStack>

      <ProductFormModal
        isOpen={createModal.isOpen}
        onClose={createModal.onClose}
        mode="create"
        userId={user?.uid}
        onSubmit={handleCreate}
      />

      <ProductFormModal
        isOpen={editModal.isOpen}
        onClose={() => {
          editModal.onClose();
          setEditingProduct(null);
        }}
        mode="edit"
        product={editingProduct}
        userId={
          cleanAccessValue((editingProduct as any)?.userId) ||
          getProductOwnerId(editingProduct) ||
          currentUserUid
        }
        onSubmit={handleUpdate}
      />
    </Box>
  );
};

export default ProductAdminPage;
